import React, { useState } from 'react';
import { FiSearch, FiTrendingUp, FiAlertCircle, FiUsers, FiBarChart2 } from 'react-icons/fi';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import AdminLayout from '../../components/admin/layout/AdminLayout';
import { useSearchAnalytics } from '../../hooks/useAnalytics';
import type { AnalyticsFilters } from '../../types/tracking';

const SearchAnalytics: React.FC = () => {
  // State for filters
  const [filters, setFilters] = useState<AnalyticsFilters>({
    days: 30,
    user_type: 'all'
  });

  // Fetch search analytics data
  const { data: analytics, isLoading, isError, error } = useSearchAnalytics(filters);

  // Handle filter changes
  const handleDaysChange = (days: 7 | 30 | 90) => {
    setFilters(prev => ({ ...prev, days }));
  };

  const handleUserTypeChange = (user_type: 'all' | 'logged_in' | 'guest') => {
    setFilters(prev => ({ ...prev, user_type }));
  };

  // Loading state
  if (isLoading) {
    return (
      <AdminLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">กำลังโหลดข้อมูล Search Analytics...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // Error state
  if (isError) {
    return (
      <AdminLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 rounded-lg border border-red-200 p-8 text-center">
            <FiAlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-red-900 mb-2">เกิดข้อผิดพลาด</h3>
            <p className="text-red-700 mb-4">
              {error instanceof Error ? error.message : 'ไม่สามารถโหลดข้อมูล Search Analytics ได้'}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              โหลดใหม่
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!analytics) {
    return null;
  }

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">สถิติการค้นหา</h1>
          <p className="text-gray-600">
            วิเคราะห์พฤติกรรมการค้นหาของผู้ใช้ และคำค้นหายอดนิยม
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            {/* Date Range Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ช่วงเวลา
              </label>
              <div className="flex gap-2">
                {[7, 30, 90].map((days) => (
                  <button
                    key={days}
                    onClick={() => handleDaysChange(days as 7 | 30 | 90)}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      filters.days === days
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {days} วัน
                  </button>
                ))}
              </div>
            </div>

            {/* User Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ประเภทผู้ใช้
              </label>
              <div className="flex gap-2">
                {[
                  { value: 'all', label: 'ทั้งหมด' },
                  { value: 'logged_in', label: 'สมาชิก' },
                  { value: 'guest', label: 'ผู้เยี่ยมชม' }
                ].map(({ value, label }) => (
                  <button
                    key={value}
                    onClick={() => handleUserTypeChange(value as 'all' | 'logged_in' | 'guest')}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      filters.user_type === value
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Searches */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <FiSearch className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <h3 className="text-sm font-medium text-gray-600 mb-1">การค้นหาทั้งหมด</h3>
            <p className="text-3xl font-bold text-gray-900">
              {analytics.total_searches.toLocaleString()}
            </p>
          </div>

          {/* Unique Users */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <FiUsers className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <h3 className="text-sm font-medium text-gray-600 mb-1">ผู้ใช้ไม่ซ้ำ</h3>
            <p className="text-3xl font-bold text-gray-900">
              {analytics.unique_users.toLocaleString()}
            </p>
          </div>

          {/* Average Results */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <FiBarChart2 className="h-6 w-6 text-purple-600" />
              </div>
            </div>
            <h3 className="text-sm font-medium text-gray-600 mb-1">ผลลัพธ์เฉลี่ย</h3>
            <p className="text-3xl font-bold text-gray-900">
              {analytics.avg_results_per_search}
            </p>
            <p className="text-xs text-gray-500 mt-1">ต่อการค้นหา</p>
          </div>

          {/* Zero Results */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-amber-100 rounded-lg">
                <FiAlertCircle className="h-6 w-6 text-amber-600" />
              </div>
            </div>
            <h3 className="text-sm font-medium text-gray-600 mb-1">ค้นหาไม่เจอ</h3>
            <p className="text-3xl font-bold text-gray-900">
              {analytics.zero_result_searches_percentage.toFixed(1)}%
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {analytics.zero_result_searches_count.toLocaleString()} ครั้ง
            </p>
          </div>
        </div>

        {/* Search Trends Chart */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-1">แนวโน้มการค้นหา</h2>
              <p className="text-sm text-gray-600">จำนวนการค้นหาในแต่ละวัน</p>
            </div>
            <FiTrendingUp className="h-6 w-6 text-blue-600" />
          </div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={analytics.search_trends}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="date"
                  stroke="#9ca3af"
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return `${date.getDate()}/${date.getMonth() + 1}`;
                  }}
                />
                <YAxis stroke="#9ca3af" tick={{ fontSize: 12 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '12px'
                  }}
                  labelFormatter={(value) => {
                    const date = new Date(value);
                    return `วันที่ ${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="count"
                  name="จำนวนการค้นหา"
                  stroke="#2563eb"
                  strokeWidth={2}
                  dot={{ fill: '#2563eb', r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Popular Keywords Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">คำค้นหายอดนิยม</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      คำค้นหา
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      จำนวน
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      ผลเฉลี่ย
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {analytics.popular_keywords.slice(0, 10).map((keyword, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium mr-2">#{index + 1}</span>
                          {keyword.query}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right font-semibold">
                        {keyword.count.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 text-right">
                        {keyword.avg_results.toFixed(1)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Zero Result Searches */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-1">คำค้นหาที่ไม่พบผลลัพธ์</h2>
                <p className="text-sm text-gray-600">ใช้ปรับปรุง SEO และเพิ่มบริษัท</p>
              </div>
              <FiAlertCircle className="h-6 w-6 text-amber-600" />
            </div>
            <div className="space-y-3">
              {analytics.zero_result_searches.length === 0 ? (
                <div className="text-center py-8">
                  <div className="p-4 bg-green-50 rounded-lg inline-flex items-center">
                    <FiTrendingUp className="h-6 w-6 text-green-600 mr-2" />
                    <span className="text-green-800 font-medium">
                      ไม่มีคำค้นหาที่ไม่พบผลลัพธ์
                    </span>
                  </div>
                </div>
              ) : (
                <div className="overflow-x-auto max-h-96 overflow-y-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-amber-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          คำค้นหา
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          จำนวนครั้ง
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {analytics.zero_result_searches.map((search, index) => (
                        <tr key={index} className="hover:bg-amber-50 transition-colors">
                          <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                            {search.query}
                          </td>
                          <td className="px-4 py-3 text-sm text-amber-700 text-right font-semibold">
                            {search.count.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer Info */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>
            ข้อมูลอัปเดตล่าสุด:{' '}
            {new Date(analytics.generated_at).toLocaleDateString('th-TH', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </p>
        </div>
      </div>
    </AdminLayout>
  );
};

export default SearchAnalytics;
